import axios from "axios";

class AssembliesService {
    static #apiUrl = process.env.REACT_APP_MIFIRMA_API

    /**
    * @author Edgar Escobedo
    * @description GET all assemblies by client
    * @date 07-03-2025
    * @param {string} timezone
    * @param {String} token
    * @param {string} [companyAssemblyId]
    * @param {string} [fromDate]
    * @param {string} [toDate]
    * @returns {Promise<{consecutive: string, data: any[], message: string, statusCode: number}>}
    * @memberof AssembliesService
    */
    static async getAssemblies(timezone, token, companyAssemblyId, fromDate, toDate) {
        let url = new URL(`${this.#apiUrl}/assemblies`)
        url.searchParams.append("timezone", timezone)
        if (companyAssemblyId) url.searchParams.append("companyAssemblyId", companyAssemblyId)
        if (toDate) url.searchParams.append("toDate", toDate)
        if (fromDate) url.searchParams.append("fromDate", fromDate)

        let response = await axios.get(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        return response.data
    }

    /**
     * @author Pedro Antonio
     * @description Obtiene las compañias para asambleas
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
     */
    static async getCompaniesAssemblies(token) {
        return axios.get(`${this.#apiUrl}/company-assemblies`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then(data => data.data)
            .catch(err => ({
                data: null,
                message: "Ocurrió un error al momento de obtener las empresas.",
                err
            }))
    }

    /**
     * @author Pedro Antonio
     * @description Crea una asamblea
     * @param {{name: string, date: Date, time: string, location: string, objective: string, notes: string }} assembly
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
     */
    static async createAssembly(assembly, token) {
        return axios.post(`${this.#apiUrl}/assembly`, {
            ...assembly
        },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).then(data => data.data)
            .catch(err => ({
                data: null,
                message: "Ocurrió un error al momento de obtener las empresas.",
                err
            }))
    }

    /**
     * @author Pedro Antonio
     * @description Trae una asambléa en base a su ID
     * @param {string} assemblyId
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
     */
    static async getAssembly(assemblyId, timezone, token) {
        let url = new URL(`${this.#apiUrl}/assembly`)
        if (assemblyId) url.searchParams.append("assemblyId", assemblyId)
        if (timezone) url.searchParams.append("timezone", timezone)
        return axios.get(url,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).then(data => data.data)
            .catch(err => ({
                data: null,
                message: "Ocurrió un error al momento de obtener las empresas.",
                err
            }))
    }

    /**
     * @author Pedro Antonio
     * @description Trae una asambléa en base a su ID
     * @param {object} assemblyId
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
     */
    static async updateAssembly(data, token) {
        return axios.put(`${this.#apiUrl}/assembly`,
            {
                ...data
            },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).then(data => data.data)
            .catch(err => ({
                data: null,
                message: "Ocurrió un error al momento de obtener las empresas.",
                err,
                statusCode: 500
            }))
    }

    /**
     * @author Pedro Antonio
     * @description Trae una asamblea en base a su ID
     * @param {{assemblyId: number, workflowExecutionId: string, status: string, statusUpdate: string}} assemblyData
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
     */
    static async deleteAssembly(assemblyData, token) {
        return axios.post(`${this.#apiUrl}/workflow-assembly`,
            {
                ...assemblyData
            },
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }).then(data => data.data)
            .catch(err => ({
                data: null,
                message: "Ocurrió un error al momento de obtener las empresas.",
                err,
                statusCode: 500
            }))
    }

    /**
 * @author  Edgar Escobedo
 * @description Change status workflow assembly
 * @param {{assemblyId: number, workflowExecutionId: string, action: string, statusUpdate: string, extraParams: {}}} assemblyData
 * @param {string} token
 * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
 * @memberof AssembliesService
 */
    static async changeStatusWorkflowAssembly(assemblyData, token) {
        let response = await axios.post(`${this.#apiUrl}/workflow-assembly`, {
            ...assemblyData
        }, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        return response.data
    }

    /**
    * @author Edgar Escobedo
    * @description GET all documents by assmblyId
    * @date 24-02-2025
    * @param {String} token
    * @returns {Object}
    * @memberof AssembliesService
    */
    static async getDocumentsAssembly(assemblyId, token) {
        let response = await axios.get(`${this.#apiUrl}/documents-assembly?assemblyId=${assemblyId}`, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        return response.data
    }


    /**
     * @author  Edgar Escobedo
     * @description Update attendance guest assembly
     * @param {{invitationId: number, attendance: boolean}} invitationData
     * @param {string} token
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
    */
    static async updateAttendanceGuest(invitationData, token) {
        let response = await axios.post(`${this.#apiUrl}/attendance-guest-assembly`, {
            ...invitationData
        }, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        return response.data
    }

    /**
     * @author Pedro Antonio
     * @description Uploades documents to assembly and update status
     * @param {File} document
     * @param {number} assemblyId
     * @param {number} id
     * @param {string} token
     * @param {string} [documentName]
     * @return {Promise<{consecutive: string, data: any, message: string, statusCode: number}>} 
     * @memberof AssembliesService
    */
    static async uploadDocumentAssembly(document, assemblyId, id, token, documentName) {
        const data = new FormData();

        data.append('file', document);
        data.append('assemblyId', assemblyId);
        if (id) data.append('id', id);
        if (documentName) data.append('documentName', documentName);

        let response = await axios.post(`${this.#apiUrl}/upload-document-assembly`, data,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            })
        return response.data
    }
}

export { AssembliesService as default }